import Navigation from '@/components/Navigation';
import Footer from '@/components/Footer';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Calendar, Clock, Phone, CheckCircle, ArrowRight, ExternalLink } from 'lucide-react';

const Prenotazioni = () => {
  const handlePrenotaOnline = () => {
    window.open('https://www.sumupbookings.com/dott-papa-peppino-salvatore', '_blank');
  };

  const vantaggiPrenotazione = [
    {
      icon: Calendar,
      titolo: "Disponibilità in Tempo Reale",
      descrizione: "Visualizza tutti gli orari disponibili e prenota immediatamente"
    },
    {
      icon: Clock,
      titolo: "Conferma Istantanea",
      descrizione: "Ricevi conferma immediata via email e SMS"
    },
    {
      icon: CheckCircle,
      titolo: "Gestione Semplice",
      descrizione: "Modifica o cancella facilmente il tuo appuntamento"
    }
  ];

  const infoImportanti = [
    "Porta con te eventuali referti medici e prescrizioni",
    "Indossa abbigliamento comodo per la valutazione",
    "Arriva 10 minuti prima dell'appuntamento",
    "Per urgenze chiama direttamente il 347.5391433"
  ];

  return (
    <div className="min-h-screen bg-background">
      <Navigation />
      
      {/* Hero Section */}
      <section className="section-padding bg-gradient-to-br from-primary/5 via-secondary/5 to-accent/5">
        <div className="container-custom">
          <div className="text-center space-y-4 mb-12">
            <Badge className="bg-primary/10 text-primary border-primary/20">
              <Calendar className="mr-2 h-4 w-4" />
              Prenotazioni
            </Badge>
            <h1 className="text-4xl lg:text-5xl font-bold">Prenota il Tuo Appuntamento</h1>
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
              Sistema di prenotazione online semplice e veloce. 
              Scegli data e orario più comodi per te.
            </p>
          </div>

          {/* CTA Principale */}
          <div className="max-w-2xl mx-auto">
            <Card className="medical-card border-primary/20">
              <CardContent className="p-8 text-center space-y-6">
                <div className="w-20 h-20 bg-primary/10 rounded-full flex items-center justify-center mx-auto">
                  <Calendar className="h-10 w-10 text-primary" />
                </div>
                <div>
                  <h2 className="text-2xl font-bold mb-2">Prenota Online</h2>
                  <p className="text-muted-foreground mb-6">
                    Accedi al nostro calendario online per prenotare il tuo appuntamento 
                    in modo rapido e sicuro
                  </p>
                </div>
                <Button 
                  size="lg" 
                  className="medical-gradient text-white w-full md:w-auto"
                  onClick={handlePrenotaOnline}
                >
                  <ExternalLink className="mr-2 h-5 w-5" />
                  Prenota Ora Online
                </Button>
                <p className="text-sm text-muted-foreground">
                  Sarai reindirizzato al nostro sistema di prenotazione sicuro
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Vantaggi */}
      <section className="section-padding">
        <div className="container-custom">
          <div className="text-center space-y-4 mb-16">
            <Badge className="bg-secondary/10 text-secondary border-secondary/20">
              Vantaggi
            </Badge>
            <h2 className="text-4xl font-bold">Perché Prenotare Online</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {vantaggiPrenotazione.map((vantaggio, index) => (
              <Card key={index} className="medical-card text-center hover:scale-105 smooth-transition">
                <CardHeader>
                  <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                    <vantaggio.icon className="h-8 w-8 text-primary" />
                  </div>
                  <CardTitle className="text-lg">{vantaggio.titolo}</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-muted-foreground">{vantaggio.descrizione}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Informazioni Importanti */}
      <section className="section-padding bg-muted/30">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {/* Cosa Portare */}
              <Card className="medical-card">
                <CardHeader>
                  <CardTitle className="text-xl">Informazioni Importanti</CardTitle>
                  <CardDescription>
                    Cosa sapere prima del tuo appuntamento
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  {infoImportanti.map((info, index) => (
                    <div key={index} className="flex items-start space-x-3">
                      <CheckCircle className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" />
                      <span className="text-sm">{info}</span>
                    </div>
                  ))}
                </CardContent>
              </Card>

              {/* Contatto Diretto */}
              <Card className="medical-card">
                <CardHeader>
                  <CardTitle className="text-xl">Preferisci Chiamare?</CardTitle>
                  <CardDescription>
                    Contattaci direttamente per prenotazioni telefoniche
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="text-center space-y-4">
                    <div className="w-16 h-16 bg-secondary/10 rounded-full flex items-center justify-center mx-auto">
                      <Phone className="h-8 w-8 text-secondary" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-primary">+39 347 5391433</p>
                      <p className="text-sm text-muted-foreground">
                        Lun-Ven: 8:00-19:00<br />
                        Sabato su appuntamento
                      </p>
                    </div>
                    <Button variant="outline" size="lg" asChild>
                      <a href="tel:+393475391433">
                        <Phone className="mr-2 h-4 w-4" />
                        Chiama Ora
                      </a>
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Servizi Disponibili */}
      <section className="section-padding">
        <div className="container-custom">
          <div className="text-center space-y-4 mb-16">
            <Badge className="bg-accent/10 text-accent border-accent/20">
              Servizi
            </Badge>
            <h2 className="text-4xl font-bold">Cosa Puoi Prenotare</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { nome: "Fisioterapia", durata: "45-60 min", prezzo: "€ 50" },
              { nome: "Riabilitazione Motoria", durata: "60 min", prezzo: "€ 60" },
              { nome: "Ginnastica Posturale", durata: "45 min", prezzo: "€ 35-50" },
              { nome: "Terapia TNPEE", durata: "45-60 min", prezzo: "€ 50-70" },
              { nome: "Disturbi Neonatali", durata: "60 min", prezzo: "€ 70-90" },
              { nome: "Terapia Manuale", durata: "45 min", prezzo: "€ 55" },
              { nome: "Massaggio Sportivo", durata: "45 min", prezzo: "€ 60" },
              { nome: "Valutazione Posturale", durata: "60 min", prezzo: "€ 80" }
            ].map((servizio, index) => (
              <Card key={index} className="medical-card text-center hover:scale-105 smooth-transition">
                <CardContent className="p-4">
                  <h3 className="font-semibold mb-2">{servizio.nome}</h3>
                  <div className="space-y-1 text-sm text-muted-foreground">
                    <div className="flex items-center justify-center space-x-1">
                      <Clock className="h-3 w-3" />
                      <span>{servizio.durata}</span>
                    </div>
                    <Badge variant="secondary" className="text-xs">
                      {servizio.prezzo}
                    </Badge>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Finale */}
      <section className="section-padding medical-gradient text-white">
        <div className="container-custom text-center space-y-8">
          <h2 className="text-4xl font-bold">Inizia il Tuo Percorso di Riabilitazione</h2>
          <p className="text-xl opacity-90 max-w-2xl mx-auto">
            Prenota ora il tuo appuntamento e inizia a prenderti cura del tuo benessere
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              size="lg" 
              variant="secondary" 
              className="bg-white text-primary hover:bg-white/90"
              onClick={handlePrenotaOnline}
            >
              <Calendar className="mr-2 h-4 w-4" />
              Prenota Online
            </Button>
            <Button size="lg" variant="outline" className="border-white text-white hover:bg-white/10" asChild>
              <a href="tel:+393475391433">
                <Phone className="mr-2 h-4 w-4" />
                Chiama: +39 347 5391433
              </a>
            </Button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Prenotazioni;